import React, { useState, useEffect } from "react";
import "../styles/RecipeList.css";
import "../styles/AIRecipeCard.css";
import Notification from "./Notification";
import { supabase } from "../supabaseClient";

interface AIRecipeProps {
  aiRecipe: string | null;
  isLoading: boolean;
}

const aiImages = [
  "/images/ai-recipe/ai1.jpg",
  "/images/ai-recipe/ai2.jpg",
  "/images/ai-recipe/ai3.jpg",
  "/images/ai-recipe/ai4.jpg",
  "/images/ai-recipe/ai5.jpg",
  "/images/ai-recipe/ai6.jpg",
  "/images/ai-recipe/ai7.jpg",
];

const AIRecipe: React.FC<AIRecipeProps> = ({ aiRecipe, isLoading }) => {
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [showNotification, setShowNotification] = useState(false);
  const [showLoginWarning, setShowLoginWarning] = useState(false);

  // ✅ Arvotaan kuva vain kerran per resepti ja tallennetaan sessionStorageen
  useEffect(() => {
    if (!isLoading && aiRecipe) {
      const hashKey = `ai-recipe-image-${aiRecipe.slice(0, 50)}`;
      const storedImage = sessionStorage.getItem(hashKey);

      if (storedImage) {
        setSelectedImage(storedImage);
      } else {
        const randomIndex = Math.floor(Math.random() * aiImages.length);
        const newImage = aiImages[randomIndex];
        setSelectedImage(newImage);
        sessionStorage.setItem(hashKey, newImage);
      }
    }
  }, [aiRecipe, isLoading]);

  const handleSaveRecipe = async () => {
    const session = await supabase.auth.getSession();
    if (!session.data.session?.user) {
      setShowLoginWarning(true);
      setTimeout(() => setShowLoginWarning(false), 3000);
      return;
    }

    const { user } = session.data.session;

    const parser = new DOMParser();
    const doc = parser.parseFromString(aiRecipe!, "text/html");
    let baseTitle =
      doc.querySelector("h3")?.textContent?.trim() || "AI-resepti";

    if (!baseTitle) {
      setShowLoginWarning(true);
      setTimeout(() => setShowLoginWarning(false), 3000);
      return;
    }

    const { data: existing, error: fetchError } = await supabase
      .from("saved_recipes")
      .select("title")
      .eq("user_id", user.id)
      .ilike("title", `${baseTitle}%`);

    if (fetchError) {
      console.error(
        "Virhe tarkistettaessa olemassa olevia reseptejä:",
        fetchError.message
      );
      return;
    }

    let finalTitle = baseTitle;
    if (existing && existing.length > 0) {
      const version = existing.length + 1;
      finalTitle = `${baseTitle} v${version}`;
    }

    const recipeData = {
      user_id: user.id,
      title: finalTitle,
      ingredients: null,
      instructions: aiRecipe,
      image_url: selectedImage,
    };

    const { error } = await supabase.from("saved_recipes").insert([recipeData]);

    if (error) {
      console.error("Virhe tallennuksessa:", error.message);
    } else {
      setShowNotification(true);
    }
  };

  if (!aiRecipe && !isLoading) return null;

  return (
    <div className="ai-recipe-wrapper">
      <div className="ai-recipe-card">
        {isLoading ? (
          <div className="spinner-container">
            <span className="spinner"></span>
          </div>
        ) : (
          <>
            <div className="ai-image-wrapper">
              <img
                src={selectedImage}
                alt="AI-resepti"
                className="ai-recipe-image"
              />
              <div className="ai-image-label">
                <span className="ai">AI</span>
                <span className="resepti">RESEPTI</span>
              </div>
            </div>

            <div
              className="ai-recipe-text"
              dangerouslySetInnerHTML={{ __html: aiRecipe! }}
            />

            <div className="save-recipe-wrapper">
              <button onClick={handleSaveRecipe} className="save-recipe-button">
                Tallenna resepti
              </button>
            </div>
          </>
        )}
      </div>

      {showNotification && (
        <Notification
          message="Resepti tallennettu!"
          onClose={() => setShowNotification(false)}
        />
      )}

      {showLoginWarning && (
        <div className="floating-warning">
          Kirjaudu sisään tallentaaksesi reseptin.
        </div>
      )}
    </div>
  );
};

export default AIRecipe;
